import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, Reveal } from "./bits";
import { scrollToId } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

const goals = [
  {
    slug: "home",
    title: "A Home",
    desc: "Turn the dream of your own space into a dated, funded plan.",
    img: "/assets/goals/home.png",
    caption: "The keys to your own place",
  },
  {
    slug: "child",
    title: "Your Child's Future",
    desc: "Education and opportunity, planned years before they arrive.",
    img: "/assets/goals/child.png",
    caption: "Options, not compromises",
  },
  {
    slug: "retirement",
    title: "Retirement",
    desc: "An income that outlives the paycheque, built quietly over time.",
    img: "/assets/goals/retirement.png",
    caption: "Freedom on your terms",
  },
  {
    slug: "freedom",
    title: "Financial Freedom",
    desc: "Work becomes a choice, not an obligation.",
    img: "/assets/goals/freedom.png",
    caption: "Time that belongs to you",
  },
  {
    slug: "legacy",
    title: "A Legacy",
    desc: "Wealth that carries your values into the next generation.",
    img: "/assets/goals/legacy.png",
    caption: "Beyond a lifetime",
  },
];

export const LifeGoals = () => {
  const [active, setActive] = useState(0);

  return (
    <section id="goals" data-testid="goals-section" className="relative bg-[#FFF7C7] py-28 md:py-40">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <Eyebrow>05 — Life Goals</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 max-w-3xl font-display text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            What are you building your <span className="italic text-emerald-600">wealth for?</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid items-start gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal className="lg:sticky lg:top-28">
            <div
              data-testid="goals-visual"
              className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-[#10202E]/10"
            >
              <AnimatePresence mode="popLayout">
                <motion.img
                  key={goals[active].slug}
                  src={goals[active].img}
                  alt={goals[active].title}
                  loading="lazy"
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.9, ease: EASE }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={goals[active].slug}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.5, ease: EASE }}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-700">
                      {goals[active].caption}
                    </span>
                    <p className="mt-1 font-display text-3xl text-[#10202E]">{goals[active].title}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </Reveal>

          <div>
            {goals.map((g, i) => (
              <button
                key={g.slug}
                type="button"
                data-testid={`goal-tab-${g.slug}`}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onClick={() => scrollToId("contact")}
                className="group block w-full border-b border-[#10202E]/15 py-7 text-left"
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="flex items-baseline gap-5">
                    <span className="font-mono text-[11px] tracking-[0.3em] text-[#8AA0B0]">
                      0{i + 1}
                    </span>
                    <span
                      className={`font-display text-3xl transition-all duration-500 sm:text-4xl ${
                        i === active
                          ? "translate-x-2 text-[#10202E]"
                          : "text-[#6E8698] group-hover:text-[#33506A]"
                      }`}
                    >
                      {g.title}
                    </span>
                  </div>
                  <ArrowUpRight
                    className={`h-5 w-5 shrink-0 transition-all duration-500 ${
                      i === active ? "-translate-y-1 translate-x-1 text-emerald-600" : "text-[#8AA0B0]"
                    }`}
                  />
                </div>
                <AnimatePresence initial={false}>
                  {i === active && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-md pl-10 pt-3 text-sm font-light leading-relaxed text-[#4E6A82]">
                        {g.desc}{" "}
                        <span className="text-emerald-700">Plan for this goal →</span>
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
